import {
  GetBookingMyMscResponse,
  InsuranceItemI,
  PassengerI,
} from '@msc-b2c/interfaces/interfaces';
import {
  InsuranceCustomDataI,
  PassengerInsurancePlaceholderCustomDataI,
  PassengersItemCustomDataI,
} from '@msc-b2c/interfaces/types';
import {
  getInsuranceEurope,
  getObsBaseIconPath,
  getObsPlaceholderIconPath,
  replacePlaceholders,
} from '@msc-b2c/shared-utils';
import { getPassengerItemsByCabinId, getPassengersInfoByItem } from './stateroomGenericUtils';

/**
 * Filters the list of insurance items by a specific cabin ID.
 *
 * @param {string} cabinId - The ID of the cabin to filter insurance items by.
 * @param {InsuranceItemI[]} insuranceItems - The list of insurance items.
 * @returns {InsuranceItemI[]} The insurance items related to the given cabin.
 */
export const getInsuranceItemsByCabinId = (
  cabinId: string,
  insuranceItems: InsuranceItemI[]
): InsuranceItemI[] => {
  return insuranceItems.filter((item) => item.relatedItem?.id === cabinId);
};

export const getInsurancePassengersInfo = (
  passengers: PassengersItemCustomDataI[]
): PassengersItemCustomDataI[] => {
  return passengers.map((passenger) => {
    return {
      ...passenger,
      insuranceCode: passenger?.insuranceCode ?? '',
    };
  });
};

/**
 * Constructs a list of insurance custom data objects for a specific cabin.
 *
 * @param {string} cabinId - The ID of the cabin.
 * @param {GetBookingMyMscResponse} bookingData - Booking data containing passengers and insurance items.
 * @returns {InsuranceCustomDataI[]} The insurance custom data enriched with basic passenger info and icon paths.
 */
export const getInsuranceCustomDataInfo = (
  cabinId: string,
  bookingData: GetBookingMyMscResponse
): InsuranceCustomDataI[] => {
  const passengers: PassengerI[] = getPassengerItemsByCabinId(cabinId, bookingData);

  const insuranceList: InsuranceItemI[] = getInsuranceItemsByCabinId(
    cabinId,
    bookingData?.insuranceItems || []
  );
  return insuranceList.map((ins) => ({
    description: ins.description,
    code: ins.code,
    supplierCode: ins.supplierCode,
    passengers: getInsurancePassengersInfo(getPassengersInfoByItem(ins, passengers)),
    icon: `${getObsBaseIconPath()}/${ins.code}.svg`,
    defaultIcon: getObsPlaceholderIconPath(),
  }));
};

/**
 * Enriches the insurance custom data with description text and passenger labels using placeholders and labels map.
 *
 * @param {string} cabinId - The ID of the cabin.
 * @param {GetBookingMyMscResponse} bookingData - Booking data including passengers and insurance items.
 * @param {PassengerInsurancePlaceholderCustomDataI} placeholders - Placeholder templates for description and labels.
 * @param {Record<string, { description?: string; specialInstructions?: string }>} labelsMap - Mapping of insurance codes to text labels.
 * @returns {InsuranceCustomDataI[]} A list of enriched insurance custom data objects.
 */
export const getInsuranceEnrichCustomDataInfo = (
  cabinId: string,
  bookingData: GetBookingMyMscResponse,
  placeholders: PassengerInsurancePlaceholderCustomDataI,
  labelsMap: Record<string, { description?: string; specialInstructions?: string }>
): InsuranceCustomDataI[] => {
  const insuranceCustomDataList = getInsuranceCustomDataInfo(cabinId, bookingData);
  insuranceCustomDataList.forEach((ins) => {
    const codeInsurance: string = ins.code;
    const isEuropeInsurance = getInsuranceEurope().includes(codeInsurance);
    ins.description = labelsMap[codeInsurance]?.description ?? ins.description;
    ins.passengers.forEach((passenger, index) => {
      if (isEuropeInsurance) {
        passenger.descriptionText = replacePlaceholders(placeholders.europeTextPlaceholder, {
          name: passenger.name,
          surname: passenger.surname,
          insuranceCode: passenger.insuranceCode || '',
        });
      } else {
        passenger.descriptionText = replacePlaceholders(placeholders.noEuropeTextPlaceholder, {
          name: passenger.name,
          surname: passenger.surname,
        });
      }
      passenger.passengerLabel = replacePlaceholders(placeholders.passengerLabelPlaceholder, {
        passengerIndex: `${index + 1}`,
      });
    });
  });
  return insuranceCustomDataList;
};
